import showcases from "./data/showcases.json";
import ShowcasePage, { showcaseHubSeo, showcaseSeo } from "./pages/ShowcasePage";
import Navbar from "./components/layout/Navbar/Navbar";
import Footer from "./components/layout/Footer/Footer";
import Home from "./pages/Home";
import CookieBanner from "./components/ui/CookieBanner/CookieBanner";
import WhatsAppButton from "./components/ui/WhatsAppButton/WhatsAppButton";
import MoreBelow from "./components/ui/MoreBelow/MoreBelow";
import PageLoader from "./components/ui/PageLoader/PageLoader";
import { lazy, Suspense, useEffect } from "react";
import { applySeo, setSeoDefaults, useSeo } from "./seo/useSeo";
import useScrollReveal from "./hooks/useScrollReveal";
import { products } from "./data/products";
import Admin from "./pages/Admin/Admin";
import InviteAccept from "./pages/Admin/InviteAccept";
import "./styles/public.css";

const AdminPreview = lazy(() => import("./pages/Admin/__Preview"));


// Off-homepage routes are code-split so the landing page doesn't ship their
// code in the initial bundle.
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const About = lazy(() => import("./pages/About"));
const Legal = lazy(() => import("./pages/Legal"));

const legalPages = {
  "/privacy-policy": "privacy",
  "/terms": "terms",
};

// The homepage used to be one long page with anchor links. Old links that
// still point at those anchors are forwarded to the page that owns them now.
const legacyAnchors = {
  "#services": "/services",
  "#services-grid": "/services",
  "#pricing": "/pricing",
  "#pricing-grid": "/pricing",
  "#industries": "/websites#industries",
  "#faq": "/pricing#faq",
  "#contact": "/contact",
  "#contact-form": "/contact#contact-form",
};


// Per-route SEO metadata. Unlisted paths fall back to the site defaults.
const seoByPath = {
  "/websites": showcaseHubSeo,
  ...Object.fromEntries(showcases.map(item => { const seo = showcaseSeo(item); return [seo.path, seo]; })),
  // The homepage intentionally has no title/description here so it inherits the
  // site-wide defaults managed from the control panel app (see setSeoDefaults).
  "/": {
    path: "/",
  },
  "/services": {
    title: "Services — Google Business Profile, Maps & Websites | Visible Online",
    description:
      "Google Business Profile setup, Google Maps optimization, and professional websites for local businesses across India.",
    path: "/services",
  },
  "/pricing": {
    title: "Pricing — Simple one-time plans | Visible Online",
    description:
      "Transparent one-time pricing for Google Business Profile setup, Maps optimization, and local business websites.",
    path: "/pricing",
  },
  "/products": {
    title: "Digital Products — Clinic Software | Visible Online",
    description:
      "Hosted software for clinics and dialysis units: Nursing One for patient management & billing, and dialysis inventory management. Set up, trained and maintained by Visible Online.",
    path: "/products",
  },
  ...Object.fromEntries(
    products.map((product) => [
      `/products/${product.slug}`,
      {
        title: product.seo.title,
        description: product.seo.description,
        path: `/products/${product.slug}`,
      },
    ])
  ),
  "/contact": {
    title: "Contact Us — Free consultation | Visible Online",
    description:
      "Get a free consultation. Reach Visible Online by phone, WhatsApp, email, or our contact form.",
    path: "/contact",
  },
  "/about": {
    title: "About Us — Visible Online",
    description:
      "Learn how Visible Online helps local businesses across India become easy to find with Google Business Profiles, websites, and custom digital solutions.",
    path: "/about",
  },
  "/privacy-policy": {
    title: "Privacy Policy — Visible Online",
    description:
      "How Visible Online collects, uses, and protects the information you share with us.",
    path: "/privacy-policy",
  },
  "/terms": {
    title: "Terms of Service — Visible Online",
    description: "The terms that govern your use of the Visible Online website and services.",
    path: "/terms",
  },
};

const routes = {
  "/": Home,
  "/services": ServicesPage,
  "/pricing": PricingPage,
  "/contact": ContactPage,
  "/about": About,
  "/products": ProductsPage,
};

const PRODUCT_PREFIX = "/products/";

function CpanelApp() {
  const path = window.location.pathname.replace(/^\/cpanel/, "").replace(/\/$/, "") || "/";
  if (path === "/__preview") {
    return (
      <Suspense fallback={null}>
        <AdminPreview />
      </Suspense>
    );
  }
  const inviteMatch = path.match(/^\/invite\/([^/]+)$/);
  if (inviteMatch) return <InviteAccept token={inviteMatch[1]} />;
  return <Admin />;
}

function PublicApp() {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  const legacyHub = path === "/our-clients" || path === "/businesses" || path === "/showcase-websites";
  const showcaseItem = showcases.find(item => item.path === path);
  const legacyShowcase = showcases.find(item => `/showcase-websites/${item.slug}` === path);
  const seo = legacyHub ? showcaseHubSeo : legacyShowcase ? showcaseSeo(legacyShowcase) : seoByPath[path] || { path, noindex: path.startsWith("/showcase-") };
  useSeo(seo);
  useScrollReveal();

  // Site-wide SEO defaults managed from the control panel (/cpanel → SEO tab).
  // Loaded lazily so Firestore stays out of the initial bundle; the built-in
  // defaults above are already applied, this just refines them once fetched.
  useEffect(() => {
    let active = true;
    import("./api/seo")
      .then(({ getSeoSettings }) => getSeoSettings())
      .then((settings) => {
        if (!active || !settings) return;
        setSeoDefaults(settings);
        applySeo(seo);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const legacyTarget = legacyHub
    ? `/websites${window.location.search}${window.location.hash}`
    : legacyShowcase ? `${legacyShowcase.path}${window.location.search}${window.location.hash}`
    : path === "/" ? legacyAnchors[window.location.hash] : null;
  if (legacyTarget) {
    window.location.replace(legacyTarget);
    return null;
  }

  let content;
  if (path === "/websites" || showcaseItem || path.startsWith("/showcase-")) {
    content = <ShowcasePage slug={path === "/websites" ? undefined : showcaseItem?.slug || path} />;
  } else if (legalPages[path]) {
    content = <Legal type={legalPages[path]} />;
  } else if (path.startsWith(PRODUCT_PREFIX)) {
    content = <ProductPage slug={path.slice(PRODUCT_PREFIX.length)} />;
  } else {
    const Page = routes[path] || Home;
    content = <Page />;
  }

  return (
    <div className="public-site">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <Navbar />
      <main id="main-content" tabIndex={-1}>
        <Suspense fallback={<PageLoader />}>{content}</Suspense>
      </main>
      <Footer />
      <MoreBelow />
      <WhatsAppButton />
      <CookieBanner />
    </div>
  );
}

function App() {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  if (path.startsWith("/cpanel")) return <CpanelApp />;
  return <PublicApp />;
}

export default App;
